import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isFounder } from "@/utils/founderAccess";

// ---------------------------------------------------------------
// /academy — Grid Induction Rail
//
// Three-step onboarding rail with progressive unlock. State
// persists in localStorage so operators can walk away and pick
// back up. All-complete triggers a confetti burst.
//
// Own palette (deep navy + electric blue) — deliberately distinct
// from the Roundtable Summit's black/gold so operators feel like
// they've moved from the "briefing stage" into the "war room".
// ---------------------------------------------------------------

const NAVY = "#1a1a2e";
const NAVY_HI = "#212140";
const CARD = "#1f1f38";
const CARD_HI = "#252546";
const BLUE = "#4fc3f7";
const BLUE_SOFT = "rgba(79,195,247,0.55)";
const BLUE_INK = "rgba(79,195,247,0.14)";
const HAIRLINE = "rgba(79,195,247,0.20)";
const MUTED = "#8892c4";
const IVORY = "#EDEEF7";
const GREEN = "#4ADE80";
const GOLD = "#D4AF37";
const GOLD_HI = "#F4D03F";
const GOLD_SOFT = "rgba(212,175,55,0.55)";
const GOLD_INK = "rgba(212,175,55,0.10)";
const API = process.env.REACT_APP_BACKEND_URL;

const SERIF = `'Playfair Display', 'Cormorant Garamond', Georgia, serif`;
const SANS = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif`;

// Persistence key — bump the suffix if the shape of STEPS ever
// changes so old visitors don't hit a stale schema.
const LS_KEY = "wa_academy_progress_v1";

const STEPS = [
  {
    id: "shield",
    number: "01",
    title: "The Shield",
    subtitle: "Amanda & Amber · Intake integrity",
    accent: "#4fc3f7",
    videoUrl: "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/f816b137a6884fc8af158ac093784202_amanda_shield_final.mp4",
    tasks: [
      "Phone line forwarded",
      "Email cockpit connected",
      "Caller ID & greeting set",
      "After-hours protocol defined",
    ],
  },
  {
    id: "dispatch",
    number: "02",
    title: "The Dispatch",
    subtitle: "Sterling · Operations velocity",
    accent: "#66d3ff",
    videoUrl: "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/48e5f4f320374c9c81568931eb5178b4_sterling_dispatch_final.mp4",
    tasks: [
      "Billing Setup",
      "Direct Invoicing",
      "Client Notes & Upsells",
      "Dispatch Lockdown",
    ],
  },
  {
    id: "growth",
    number: "03",
    title: "The Growth",
    subtitle: "Vance & Sloan · Market resonance",
    accent: "#7dd3ff",
    videoUrl: "https://customer-assets-4nw71qhi.emergentagent.net/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/e3f6d24af99841e68b5da1b077f98e82_vance_resonance_final.mp4",
    tasks: [
      "Content engine activated",
      "Content calendar populated",
      "Sloan's library indexed",
      "Neighborhood visibility live",
    ],
  },
];

// Compressed keyword router for the Academy's Direct Council box.
// Same "highest hit count wins" logic as /roundtable — deliberately
// duplicated here so Academy stays self-contained.
const ACADEMY_KEYWORDS = {
  ALFRED:   ["strategy", "vision", "positioning", "market", "grow", "plan", "last 50 miles"],
  AMANDA:   ["leads", "lead", "intake", "onboarding", "capture", "new client", "regional"],
  STERLING: ["operations", "crew", "dispatch", "schedule", "billing", "invoice", "route"],
  VANCE:    ["marketing", "content", "social", "ads", "story", "seo", "brand", "reviews"],
  AMBER:    ["quality", "audit", "compliance", "standards", "friction", "review"],
  SLOAN:    ["plant", "lawn", "grass", "fertilizer", "tree", "prune", "soil", "irrigation", "weed", "shrub", "landscape"],
};
function matchCouncil(question) {
  const q = (question || "").toLowerCase();
  if (!q.trim()) return "ALFRED";
  let best = { key: "ALFRED", score: 0 };
  for (const [key, kws] of Object.entries(ACADEMY_KEYWORDS)) {
    const score = kws.reduce((n, kw) => (q.includes(kw) ? n + 1 : n), 0);
    if (score > best.score) best = { key, score };
  }
  return best.score > 0 ? best.key : "ALFRED";
}
const MEMBER_TITLES = {
  ALFRED: "Chief Visionary Officer",
  AMANDA: "VP of Lead Generation",
  STERLING: "Director of Operations",
  VANCE: "VP of Market Strategy",
  AMBER: "Quality Assurance Lead",
  SLOAN: "Technical Director",
};

// ---------- Root ----------
export default function Academy() {
  const founder = isFounder();

  // Progress state — map of `${stepId}:${taskIndex}` → true.
  const [checks, setChecks] = useState(() => loadProgress());
  useEffect(() => saveProgress(checks), [checks]);

  const stepStates = useMemo(() => STEPS.map((s, idx) => {
    const total = s.tasks.length;
    const done = s.tasks.reduce((n, _, ti) => (checks[`${s.id}:${ti}`] ? n + 1 : n), 0);
    const complete = done === total;
    const isLast = idx === STEPS.length - 1;
    return { step: s, index: idx, total, done, complete, isLast };
  }), [checks]);
  // Progressive unlock — a step is locked until the PREVIOUS one
  // is fully checked. Step 1 is always unlocked.
  const isLocked = (idx) => idx === 0 ? false : !stepStates[idx - 1].complete;
  const allComplete = stepStates.every((s) => s.complete);

  // Fire confetti exactly once per "arrival at all-complete".
  const [confettiKey, setConfettiKey] = useState(0);
  const wasComplete = useRef(allComplete);
  useEffect(() => {
    if (allComplete && !wasComplete.current) setConfettiKey((k) => k + 1);
    wasComplete.current = allComplete;
  }, [allComplete]);

  const [expandedId, setExpandedId] = useState(STEPS[0].id);
  const toggle = (idx) => (open) => {
    if (isLocked(idx)) return;
    setExpandedId((cur) => (open === true ? STEPS[idx].id : cur === STEPS[idx].id ? null : STEPS[idx].id));
  };
  const setTask = (stepId, taskIdx, v) =>
    setChecks((c) => ({ ...c, [`${stepId}:${taskIdx}`]: v }));

  const resetAll = () => {
    setChecks({});
    setExpandedId(STEPS[0].id);
  };

  return (
    <div
      data-testid="academy-root"
      style={{
        minHeight: "100vh",
        background: `linear-gradient(180deg, ${NAVY} 0%, #14142a 100%)`,
        color: IVORY,
        fontFamily: SANS,
      }}
    >
      <style>{ACADEMY_CSS}</style>

      {/* ============ STICKY PROGRESS RAIL ============ */}
      <ProgressRail steps={stepStates} founder={founder} />

      {/* ============ HERO ============ */}
      <section
        style={{
          padding: "44px 20px 24px",
          maxWidth: 960,
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <div
          data-testid="academy-kicker"
          style={{
            display: "inline-block",
            padding: "6px 14px",
            border: `1px solid ${BLUE_SOFT}`,
            color: BLUE,
            fontSize: 11,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 999,
            marginBottom: 18,
          }}
        >
          Grid Induction · Rail
        </div>
        <h1
          data-testid="academy-title"
          style={{
            fontFamily: SERIF,
            fontSize: "clamp(2.2rem, 5vw, 3.4rem)",
            fontWeight: 700,
            margin: 0,
            color: IVORY,
            letterSpacing: "-0.005em",
            lineHeight: 1.1,
          }}
        >
          The Academy
        </h1>
        <p
          style={{
            marginTop: 14,
            color: MUTED,
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: "1.05rem",
            maxWidth: 640,
            margin: "14px auto 0",
            lineHeight: 1.55,
          }}
        >
          Three steps between you and a running Command Center. Move through
          the rail — the Grid unlocks as you go.
        </p>
      </section>

      {/* ============ STEP CARDS ============ */}
      <section
        data-testid="academy-steps"
        style={{
          maxWidth: 960,
          margin: "0 auto",
          padding: "16px 20px 40px",
          display: "grid",
          gap: 18,
        }}
      >
        {STEPS.map((s, idx) => (
          <StepCard
            key={s.id}
            step={s}
            state={stepStates[idx]}
            locked={isLocked(idx)}
            expanded={expandedId === s.id}
            onToggle={toggle(idx)}
            onCheck={(ti, v) => setTask(s.id, ti, v)}
            checks={checks}
          />
        ))}

        {/* Reset control — shows once the operator has any progress
            saved so returning visitors can nuke the rail. */}
        {Object.keys(checks).length > 0 && (
          <div style={{ textAlign: "center", marginTop: 6 }}>
            <button
              type="button"
              data-testid="academy-reset"
              onClick={resetAll}
              style={{
                background: "transparent",
                color: MUTED,
                border: `1px solid ${HAIRLINE}`,
                padding: "10px 16px",
                borderRadius: 999,
                fontSize: 11,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Reset Progress
            </button>
          </div>
        )}
      </section>

      {/* ============ DIRECT COUNCIL ACCESS ============ */}
      <DirectCouncil />

      {/* ============ FOOTER ============ */}
      <footer
        style={{
          padding: "28px 20px 120px",
          borderTop: `1px solid ${HAIRLINE}`,
          textAlign: "center",
          marginTop: 40,
        }}
      >
        <div style={{ color: BLUE, fontFamily: SERIF, fontStyle: "italic", fontSize: "0.9rem", letterSpacing: "0.06em" }}>
          © Wingman Alfred &nbsp;•&nbsp; Grid Induction Rail
        </div>
        <div style={{ marginTop: 8, color: MUTED, fontSize: 12 }}>
          Target validation: <span style={{ color: IVORY, fontWeight: 600 }}>August 28</span>
          &nbsp;·&nbsp;
          <Link to="/roundtable" style={{ color: BLUE, textDecoration: "none" }}>Return to Council</Link>
        </div>
      </footer>

      {/* Confetti — re-mounts on each key change so a re-completion
          replays the burst if the operator un-checks and re-checks. */}
      {confettiKey > 0 && <Confetti key={confettiKey} />}
    </div>
  );
}

// ---------- Progress Rail ----------
function ProgressRail({ steps, founder }) {
  const total = steps.reduce((n, s) => n + s.total, 0);
  const done = steps.reduce((n, s) => n + s.done, 0);
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div
      data-testid="academy-progress-rail"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "rgba(20,20,40,0.92)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: `1px solid ${HAIRLINE}`,
      }}
    >
      <div
        style={{
          maxWidth: 960,
          margin: "0 auto",
          padding: "14px 20px",
          display: "flex",
          gap: 14,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Step pips */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flex: "1 1 260px" }}>
          {steps.map((s, idx) => (
            <div key={s.step.id} style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
              <div
                data-testid={`academy-pip-${s.step.id}`}
                style={{
                  minWidth: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: s.complete ? GREEN : (s.done > 0 ? BLUE : "transparent"),
                  border: `2px solid ${s.complete ? GREEN : BLUE_SOFT}`,
                  color: s.complete ? "#062B14" : IVORY,
                  fontFamily: SERIF,
                  fontWeight: 800,
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 260ms ease",
                }}
                aria-label={`Step ${s.step.number} · ${s.done}/${s.total} tasks`}
              >
                {s.complete ? "✓" : s.step.number}
              </div>
              {idx < steps.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    background: s.complete ? BLUE : HAIRLINE,
                    minWidth: 20,
                    borderRadius: 2,
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Percent + founder badge */}
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div
            data-testid="academy-progress-pct"
            style={{
              color: BLUE,
              fontFamily: SANS,
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
            }}
          >
            {pct}% · {done}/{total}
          </div>
          {founder && (
            <span
              data-testid="academy-founder-badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 999,
                background: BLUE_INK,
                border: `1px solid ${BLUE}`,
                color: BLUE,
                fontSize: 10,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                fontWeight: 800,
              }}
            >
              <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>★</span>
              Intelligence Tier
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Step Card ----------
function StepCard({ step, state, locked, expanded, onToggle, onCheck, checks }) {
  return (
    <article
      data-testid={`academy-step-${step.id}`}
      style={{
        background: state.complete ? CARD_HI : CARD,
        border: `1px solid ${state.complete ? GREEN : (expanded ? BLUE : HAIRLINE)}`,
        borderRadius: 14,
        padding: "18px 20px",
        opacity: locked ? 0.55 : 1,
        transition: "border-color 260ms ease, background 260ms ease, opacity 260ms ease",
        boxShadow: expanded && !locked ? `0 30px 60px -30px rgba(79,195,247,0.35)` : "none",
      }}
    >
      <button
        type="button"
        onClick={() => onToggle()}
        disabled={locked}
        data-testid={`academy-step-toggle-${step.id}`}
        aria-expanded={expanded}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          color: "inherit",
          padding: 0,
          cursor: locked ? "not-allowed" : "pointer",
          textAlign: "left",
          display: "flex",
          gap: 16,
          alignItems: "center",
          minHeight: 44,
        }}
      >
        <span
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: state.complete ? GREEN : (locked ? "#2a2a4a" : NAVY_HI),
            color: state.complete ? "#062B14" : (locked ? MUTED : BLUE),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: SERIF,
            fontWeight: 800,
            fontSize: 18,
            flexShrink: 0,
            border: `1px solid ${state.complete ? GREEN : HAIRLINE}`,
          }}
        >
          {state.complete ? "✓" : step.number}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(1.3rem, 3.4vw, 1.55rem)",
              fontWeight: 700,
              color: IVORY,
              lineHeight: 1.15,
              letterSpacing: "-0.005em",
            }}
          >
            {step.title}
          </div>
          <div
            style={{
              marginTop: 4,
              color: MUTED,
              fontSize: "0.92rem",
              letterSpacing: "0.02em",
            }}
          >
            {step.subtitle}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <div
            style={{
              color: state.complete ? GREEN : BLUE,
              fontSize: 11,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              fontWeight: 800,
            }}
          >
            {locked ? "Locked" : `${state.done}/${state.total}`}
          </div>
          <div style={{ color: MUTED, fontSize: 18, lineHeight: 1 }} aria-hidden>
            {expanded ? "−" : "+"}
          </div>
        </div>
      </button>

      {expanded && !locked && (
        <div
          data-testid={`academy-step-body-${step.id}`}
          style={{ marginTop: 18, display: "grid", gap: 14 }}
        >
          {/* Step walkthrough video — 16:9, responsive, controls
              on. Only mounted when the step is expanded + unlocked
              so we never preload three streams at once. */}
          <div
            data-testid={`academy-video-${step.id}`}
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "16 / 9",
              background: `linear-gradient(160deg, ${NAVY_HI} 0%, ${NAVY} 100%)`,
              border: `1px solid ${BLUE_SOFT}`,
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: `0 24px 60px -30px rgba(79,195,247,0.35)`,
            }}
          >
            <video
              key={step.videoUrl}
              src={step.videoUrl}
              controls
              playsInline
              preload="metadata"
              data-testid={`academy-video-el-${step.id}`}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                background: "#000",
                objectFit: "cover",
              }}
            />
          </div>

          {/* Task list — every step now ships the interactive
              Action Drawer accordion. Shield / Dispatch / Growth
              each render their own surgical content per drawer
              index through StepDrawerContent. */}
          <ActionRail
            tasks={step.tasks}
            checks={checks}
            stepId={step.id}
            onCheck={onCheck}
          />

          {state.complete && (
            <div
              style={{
                marginTop: 4,
                padding: "12px 14px",
                background: "rgba(74,222,128,0.10)",
                border: `1px solid rgba(74,222,128,0.55)`,
                borderRadius: 10,
                color: GREEN,
                fontSize: "0.9rem",
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              ✓ Step {step.number} complete
              {state.isLast ? " · Grid induction complete" : " · Next rail unlocked"}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ---------- Direct Council Access ----------
function DirectCouncil() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [matched, setMatched] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState("");

  const ask = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const q = question.trim();
    if (!q || loading) return;
    const member = matchCouncil(q);
    setMatched(member);
    setAnswer(null);
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/roundtable/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: q,
          matched_member: member,
          company: "",
          contact: "Operator",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Council unreachable (HTTP ${res.status})`);
      setAnswer({
        member: data.member || member,
        title: data.title || MEMBER_TITLES[member] || "",
        response: data.response || "",
      });
    } catch (err) {
      setError(err.message || "Council unreachable. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="academy-direct-council"
      style={{
        maxWidth: 820,
        margin: "40px auto 0",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          background: CARD,
          border: `1px solid ${BLUE}`,
          borderRadius: 14,
          padding: "26px 24px",
          boxShadow: `0 30px 70px -30px rgba(79,195,247,0.35)`,
        }}
      >
        <div
          style={{
            color: BLUE,
            fontSize: 11,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 800,
            marginBottom: 8,
          }}
        >
          Direct Council Access
        </div>
        <h2
          style={{
            fontFamily: SERIF,
            fontSize: "clamp(1.4rem, 3.4vw, 1.7rem)",
            color: IVORY,
            margin: "0 0 6px",
            fontWeight: 700,
          }}
        >
          Ask the Council.
        </h2>
        <p style={{ color: MUTED, marginTop: 0, marginBottom: 16, lineHeight: 1.55 }}>
          Type a question — it&apos;ll route to the council member whose lane
          owns the answer, same routing as the Roundtable.
        </p>

        <form onSubmit={ask} style={{ display: "grid", gap: 12 }}>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. How do I prune my hedges in fall?"
            disabled={loading}
            data-testid="academy-direct-input"
            rows={3}
            className="ac-textarea"
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: NAVY,
              color: IVORY,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 10,
              padding: "14px 16px",
              fontSize: "1rem",
              lineHeight: 1.5,
              fontFamily: SANS,
              outline: "none",
              resize: "vertical",
              minHeight: 96,
              transition: "border-color 220ms ease, box-shadow 220ms ease",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              data-testid="academy-direct-submit"
              disabled={loading || !question.trim()}
              style={{
                minHeight: 48,
                background: BLUE,
                color: "#062038",
                padding: "13px 22px",
                fontSize: "0.85rem",
                border: "none",
                borderRadius: 10,
                cursor: loading || !question.trim() ? "not-allowed" : "pointer",
                fontWeight: 800,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                opacity: loading || !question.trim() ? 0.55 : 1,
                boxShadow: `0 14px 30px -12px ${BLUE}`,
              }}
            >
              {loading ? "Routing…" : "Ask the Council"}
            </button>
          </div>
        </form>

        {loading && matched && (
          <div
            data-testid="academy-direct-typing"
            style={{
              marginTop: 16,
              color: MUTED,
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: BLUE, fontFamily: SERIF, fontWeight: 700 }}>
              {matched.charAt(0) + matched.slice(1).toLowerCase()}
            </span>
            <span>is reviewing your question</span>
            <span>
              <span className="ac-dot" />
              <span className="ac-dot" />
              <span className="ac-dot" />
            </span>
          </div>
        )}
        {error && !loading && (
          <div
            data-testid="academy-direct-error"
            style={{
              marginTop: 16,
              background: "rgba(239,68,68,0.10)",
              border: "1px solid rgba(239,68,68,0.55)",
              borderRadius: 10,
              padding: "12px 14px",
              color: IVORY,
              fontSize: "0.95rem",
            }}
          >
            {error}
          </div>
        )}
        {answer && !loading && (
          <div
            data-testid="academy-direct-answer"
            style={{
              marginTop: 20,
              background: NAVY_HI,
              borderLeft: `4px solid ${BLUE}`,
              borderTop: `1px solid ${HAIRLINE}`,
              borderRight: `1px solid ${HAIRLINE}`,
              borderBottom: `1px solid ${HAIRLINE}`,
              borderRadius: 8,
              padding: "18px 20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
              <span style={{ fontFamily: SERIF, color: BLUE, fontSize: "1.4rem", fontWeight: 700, lineHeight: 1 }}>
                {answer.member.charAt(0) + answer.member.slice(1).toLowerCase()}
              </span>
              <span style={{ color: MUTED, fontSize: "0.92rem" }}>· {answer.title}</span>
            </div>
            <div
              style={{ color: IVORY, fontSize: "1rem", lineHeight: 1.65, whiteSpace: "pre-wrap" }}
            >
              {answer.response}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Confetti ----------
function Confetti() {
  // 60 pieces, staggered, each colored from the accent palette.
  const pieces = useMemo(() => {
    const colors = [BLUE, "#7dd3ff", GREEN, "#F5F1E3", "#C9A84C"];
    return Array.from({ length: 60 }, (_, i) => ({
      i,
      left: Math.random() * 100,
      delay: Math.random() * 0.4,
      dur: 2.4 + Math.random() * 1.6,
      color: colors[i % colors.length],
      size: 6 + Math.random() * 8,
    }));
  }, []);
  return (
    <div
      data-testid="academy-confetti"
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 40,
        overflow: "hidden",
      }}
    >
      {pieces.map((p) => (
        <span
          key={p.i}
          style={{
            position: "absolute",
            top: -20,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: p.i % 3 === 0 ? "50%" : 2,
            animation: `ac-fall ${p.dur}s ease-in ${p.delay}s forwards`,
            transform: "translateY(-20px) rotate(0deg)",
          }}
        />
      ))}
    </div>
  );
}

// ---------- Action Rail (accordion drawers for all 3 steps) ----------
// One rail per step. Manages its own accordion state (one drawer
// at a time). Pushes completion up through the same `onCheck`
// handler the checkbox rows used, so the unlock chain +
// localStorage persistence + progress rail all keep working
// unchanged. Drawers within a step are also sequentially gated:
// drawer N is locked until drawer N-1 is checked.
//
// Per-drawer completion labels are looked up in COMPLETE_LABELS
// keyed by stepId; fall back to a shared "I have completed this"
// so any future step slots in cleanly.
const COMPLETE_LABELS = {
  shield: [
    "I have completed this",
    "I have completed this",
    "I have completed this",
    "I have completed this",
  ],
  dispatch: [
    "Billing Connected",
    "Invoicing Armed",
    "Intel System Active",
    "Lock In Dispatch",
  ],
  growth: [
    "Engine Started",
    "Socials Mapped",
    "Library Indexed",
    "Lock In Growth",
  ],
};

function ActionRail({ tasks, checks, stepId, onCheck }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [flashIdx, setFlashIdx] = useState(null);

  const isDrawerLocked = (idx) =>
    idx > 0 && !checks[`${stepId}:${idx - 1}`];

  const toggle = (idx) => {
    if (isDrawerLocked(idx)) return;
    setOpenIdx((cur) => (cur === idx ? null : idx));
  };
  const markComplete = (idx) => {
    onCheck(idx, true);
    setFlashIdx(idx);
    // Collapse the drawer after the green flash lands so the
    // operator visually "checks off" the row.
    window.setTimeout(() => setFlashIdx(null), 900);
    setOpenIdx(null);
  };

  const labels = COMPLETE_LABELS[stepId] || [];

  return (
    <div
      style={{ display: "grid", gap: 10 }}
      data-testid={`${stepId}-action-rail`}
    >
      {tasks.map((task, ti) => (
        <ActionDrawer
          key={task}
          index={ti}
          stepId={stepId}
          task={task}
          done={!!checks[`${stepId}:${ti}`]}
          open={openIdx === ti}
          flash={flashIdx === ti}
          locked={isDrawerLocked(ti)}
          completeLabel={labels[ti] || "I have completed this"}
          checks={checks}
          onToggle={() => toggle(ti)}
          onComplete={() => markComplete(ti)}
        />
      ))}
    </div>
  );
}

function ActionDrawer({
  index,
  stepId,
  task,
  done,
  open,
  flash,
  locked,
  completeLabel,
  checks,
  onToggle,
  onComplete,
}) {
  const rowBg = flash
    ? "rgba(74,222,128,0.22)"
    : done
    ? BLUE_INK
    : locked
    ? "rgba(255,255,255,0.015)"
    : "rgba(255,255,255,0.02)";
  const rowBorder = flash
    ? GREEN
    : done
    ? BLUE_SOFT
    : open
    ? GOLD_SOFT
    : HAIRLINE;

  return (
    <div
      data-testid={`action-drawer-${stepId}-${index}`}
      style={{
        borderRadius: 10,
        overflow: "hidden",
        // Left rail only when open so the drawer visually anchors
        // back to the trigger row above it.
        borderLeft: open ? `3px solid ${GOLD}` : "3px solid transparent",
        transition: "border-color 220ms ease",
        opacity: locked ? 0.55 : 1,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={locked}
        data-testid={`drawer-trigger-${stepId}-${index}`}
        aria-expanded={open}
        style={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          minHeight: 56,
          background: rowBg,
          border: `1px solid ${rowBorder}`,
          borderRadius: open ? "10px 10px 0 0" : 10,
          cursor: locked ? "not-allowed" : "pointer",
          transition:
            "background 260ms ease, border-color 260ms ease, border-radius 200ms ease",
        }}
      >
        <span
          data-testid={`drawer-check-${stepId}-${index}`}
          aria-hidden
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: done ? GOLD : "transparent",
            border: `2px solid ${done ? GOLD : (open ? GOLD_SOFT : HAIRLINE)}`,
            color: done ? "#0F0A00" : "transparent",
            fontSize: 14,
            fontWeight: 900,
            flexShrink: 0,
            transition: "all 220ms ease",
          }}
        >
          {done ? "✓" : ""}
        </span>
        <span
          style={{
            flex: 1,
            color: done ? MUTED : IVORY,
            fontSize: "0.98rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            textDecorationLine: done ? "line-through" : "none",
            textDecorationColor: GOLD_SOFT,
          }}
        >
          {task}
        </span>
        {locked ? (
          <span
            aria-hidden
            style={{
              color: MUTED,
              fontSize: 10,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            Locked
          </span>
        ) : (
          <span
            aria-hidden
            style={{
              color: open ? GOLD : MUTED,
              fontSize: 14,
              lineHeight: 1,
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 240ms ease, color 240ms ease",
              flexShrink: 0,
            }}
          >
            ▸
          </span>
        )}
      </button>

      <div
        data-testid={`drawer-content-${stepId}-${index}`}
        style={{
          maxHeight: open ? 1600 : 0,
          overflow: "hidden",
          transition:
            "max-height 380ms ease, padding 240ms ease, background 240ms ease",
          background: NAVY_HI,
          border: open ? `1px solid ${GOLD_SOFT}` : "1px solid transparent",
          borderTop: open ? "none" : "1px solid transparent",
          borderRadius: "0 0 10px 10px",
        }}
      >
        <div style={{ padding: open ? "18px 18px 20px" : "0 18px" }}>
          <StepDrawerContent
            stepId={stepId}
            index={index}
            task={task}
            checks={checks}
          />
          <button
            type="button"
            data-testid={`complete-btn-${stepId}-${index}`}
            onClick={onComplete}
            disabled={done}
            style={{
              marginTop: 18,
              width: "100%",
              minHeight: 48,
              padding: "14px 20px",
              fontSize: 15,
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: done ? MUTED : "#0F0A00",
              background: done
                ? "rgba(255,255,255,0.04)"
                : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_HI} 100%)`,
              border: done ? `1px solid ${HAIRLINE}` : "none",
              borderRadius: 10,
              cursor: done ? "default" : "pointer",
              boxShadow: done ? "none" : `0 14px 30px -12px ${GOLD_SOFT}`,
              transition: "transform 160ms ease, box-shadow 160ms ease",
            }}
            onMouseDown={(e) => {
              if (!done) e.currentTarget.style.transform = "translateY(1px)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {done ? "✓ Task complete" : completeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Drawer surgical content (step-scoped router) ----------
function StepDrawerContent({ stepId, index, task, checks }) {
  if (stepId === "shield")
    return <ShieldDrawerContent index={index} task={task} />;
  if (stepId === "dispatch")
    return <DispatchDrawerContent index={index} task={task} checks={checks} />;
  if (stepId === "growth")
    return <GrowthDrawerContent index={index} task={task} checks={checks} />;
  return null;
}

// ---------- Drawer surgical content (index-switched) ----------
function ShieldDrawerContent({ index, task }) {
  switch (index) {
    case 0:
      return <PhoneForwardDrawer />;
    case 1:
      return <EmailCockpitDrawer />;
    case 2:
      return <GreetingDrawer />;
    case 3:
      return <AfterHoursDrawer />;
    default:
      return (
        <div>
          <DrawerHeading>{task}</DrawerHeading>
          <p style={{ color: MUTED, margin: 0 }}>Details coming soon.</p>
        </div>
      );
  }
}

function DrawerHeading({ children }) {
  return (
    <h4
      style={{
        margin: "0 0 8px",
        color: IVORY,
        fontFamily: SERIF,
        fontSize: "1.15rem",
        fontWeight: 700,
        letterSpacing: "-0.005em",
      }}
    >
      {children}
    </h4>
  );
}

function DrawerBody({ children }) {
  return (
    <p style={{ margin: "0 0 14px", color: "#C7CBE0", fontSize: "0.95rem", lineHeight: 1.6 }}>
      {children}
    </p>
  );
}

// Placeholder audio pill — real clips will be swapped in later.
function AudioPlaceholder({ index }) {
  return (
    <div
      data-testid={`audio-placeholder-${index}`}
      style={{
        marginTop: 14,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        borderRadius: 999,
        background: "rgba(212,175,55,0.08)",
        border: `1px solid ${GOLD_SOFT}`,
        color: GOLD,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>▶</span>
      🎧 Amanda explains (10s)
    </div>
  );
}

// ---- Task 1 · Phone Line Forwarded ----
function PhoneForwardDrawer() {
  return (
    <div>
      <DrawerHeading>Forward Your Phone Line</DrawerHeading>
      <DrawerBody>
        From your business phone, dial the code below to forward all calls to
        your Grid number.
      </DrawerBody>
      <div
        style={{
          background: NAVY,
          border: `1px solid ${GOLD_SOFT}`,
          borderRadius: 8,
          padding: "14px 16px",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          fontSize: "1.05rem",
          color: GOLD,
          letterSpacing: "0.03em",
          whiteSpace: "nowrap",
          overflowX: "auto",
        }}
      >
        *72 + [Your Grid Number]
      </div>
      <a
        href="tel:*72"
        data-testid="dial-now-btn"
        style={{
          marginTop: 14,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 22px",
          background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_HI} 100%)`,
          color: "#0F0A00",
          borderRadius: 10,
          textDecoration: "none",
          fontWeight: 800,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontSize: 13,
          boxShadow: `0 12px 24px -10px ${GOLD_SOFT}`,
        }}
      >
        <span aria-hidden>📞</span> Dial Now
      </a>
      <div style={{ marginTop: 12, color: MUTED, fontSize: 12 }}>
        To cancel forwarding later, dial <span style={{ color: IVORY, fontFamily: "ui-monospace, monospace" }}>*73</span>
      </div>
      <AudioPlaceholder index={1} />
    </div>
  );
}

// ---- Task 2 · Email Cockpit Connected ----
function EmailCockpitDrawer() {
  const rows = [
    { key: "imap", label: "IMAP Server", value: "imap.zoho.com" },
    { key: "imap-port", label: "IMAP Port", value: "993" },
    { key: "smtp", label: "SMTP Server", value: "smtp.zoho.com" },
    { key: "smtp-port", label: "SMTP Port", value: "465" },
    { key: "proto", label: "Protocol", value: "SSL/TLS" },
  ];
  return (
    <div>
      <DrawerHeading>Connect Your Email Cockpit</DrawerHeading>
      <DrawerBody>
        Copy these settings into your email provider (Zoho Mail or Mailgun) to
        route inbound messages to your Grid.
      </DrawerBody>
      <div
        style={{
          display: "grid",
          gap: 8,
          background: NAVY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 10,
          padding: 10,
        }}
      >
        {rows.map((r) => (
          <CopyRow key={r.key} testKey={r.key} label={r.label} value={r.value} />
        ))}
      </div>
      <AudioPlaceholder index={2} />
    </div>
  );
}

function CopyRow({ testKey, label, value, mono = false }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Fallback: silently no-op. Value is visible on screen already.
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  const isBlock = mono || value.includes("\n");
  return (
    <div
      style={{
        display: "flex",
        alignItems: isBlock ? "flex-start" : "center",
        gap: 10,
        padding: "10px 12px",
        background: "rgba(255,255,255,0.02)",
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 8,
        flexWrap: "wrap",
      }}
    >
      <span style={{ color: MUTED, fontSize: 12, minWidth: 110, letterSpacing: "0.04em", marginTop: isBlock ? 6 : 0 }}>
        {label}
      </span>
      {isBlock ? (
        <pre
          style={{
            flex: 1,
            minWidth: 200,
            margin: 0,
            color: GOLD,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.85rem",
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            background: NAVY,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 6,
            padding: "8px 10px",
          }}
        >
          {value}
        </pre>
      ) : (
        <span
          style={{
            flex: 1,
            color: IVORY,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.95rem",
            wordBreak: "break-all",
          }}
        >
          {value}
        </span>
      )}
      <button
        type="button"
        onClick={copy}
        data-testid={`copy-btn-${testKey}`}
        style={{
          background: copied ? GREEN : "transparent",
          color: copied ? "#062B14" : GOLD,
          border: `1px solid ${copied ? GREEN : GOLD_SOFT}`,
          padding: "6px 12px",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 800,
          borderRadius: 6,
          cursor: "pointer",
          transition: "all 200ms ease",
          alignSelf: isBlock ? "flex-start" : "auto",
          marginTop: isBlock ? 6 : 0,
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// ---- Task 3 · Caller ID & Greeting ----
function GreetingDrawer() {
  const startRecord = async () => {
    // Politely attempt a media permission handshake so browsers
    // that CAN record show the correct prompt; if it fails or the
    // user denies, we fall back to the onboarding notice below.
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => {});
      }
    } catch {
      /* noop */
    }
    window.alert(
      "Recording feature coming soon — your Grid admin will set this up during onboarding."
    );
  };
  return (
    <div>
      <DrawerHeading>Record Your Custom Greeting</DrawerHeading>
      <DrawerBody>
        Record a 15–30 second greeting that callers will hear. Speak clearly and
        include your name and business.
      </DrawerBody>
      <button
        type="button"
        onClick={startRecord}
        data-testid="record-greeting-btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 22px",
          background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_HI} 100%)`,
          color: "#0F0A00",
          border: "none",
          borderRadius: 10,
          fontWeight: 800,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontSize: 13,
          cursor: "pointer",
          boxShadow: `0 12px 24px -10px ${GOLD_SOFT}`,
        }}
      >
        <span aria-hidden>🎙</span> Record Greeting
      </button>
      <blockquote
        style={{
          margin: "16px 0 0",
          padding: "14px 16px",
          borderLeft: `3px solid ${GOLD}`,
          background: "rgba(212,175,55,0.06)",
          color: IVORY,
          fontStyle: "italic",
          fontFamily: SERIF,
          fontSize: "1rem",
          lineHeight: 1.55,
          borderRadius: "0 8px 8px 0",
        }}
      >
        “Hi, you’ve reached <span style={{ color: GOLD }}>[Your Name]</span> at
        {" "}
        <span style={{ color: GOLD }}>[Business]</span>. I’m currently unavailable
        but your call is important to me. Please leave a message and I’ll get
        back to you shortly.”
      </blockquote>
      <AudioPlaceholder index={3} />
    </div>
  );
}

// ---- Task 4 · After-hours protocol defined ----
function AfterHoursDrawer() {
  return (
    <div>
      <DrawerHeading>Define Your After-Hours Protocol</DrawerHeading>
      <DrawerBody>
        Decide how the Grid handles calls, emails, and messages once your shop
        closes. Amanda will follow the routing you pick here — she never sleeps.
      </DrawerBody>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          display: "grid",
          gap: 8,
        }}
      >
        {[
          "Set your working hours (e.g. Mon–Fri, 8am–6pm)",
          "Choose overflow: voicemail, text-back, or on-call cell",
          "Draft an after-hours auto-reply for email + SMS",
          "Flag emergencies so Amanda escalates immediately",
        ].map((line) => (
          <li
            key={line}
            style={{
              padding: "10px 12px",
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 8,
              color: IVORY,
              fontSize: "0.93rem",
              lineHeight: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}
          >
            <span style={{ color: GOLD, fontWeight: 800, flexShrink: 0 }}>›</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <AudioPlaceholder index={4} />
    </div>
  );
}

// ---------- Dispatch drawer bodies (Step 02 · Sterling) ----------
function DispatchDrawerContent({ index, task, checks }) {
  switch (index) {
    case 0:
      return <BillingSetupDrawer />;
    case 1:
      return <DirectInvoicingDrawer />;
    case 2:
      return <ClientIntelDrawer />;
    case 3:
      return <DispatchConfirmDrawer checks={checks} />;
    default:
      return (
        <div>
          <DrawerHeading>{task}</DrawerHeading>
        </div>
      );
  }
}

// ---- Dispatch 1 · Billing Setup ----
function BillingSetupDrawer() {
  const [key, setKey] = useState("");
  const [testState, setTestState] = useState("idle"); // idle | testing | ok
  const paste = async () => {
    try {
      const v = await navigator.clipboard.readText();
      if (v) setKey(v);
    } catch {
      /* clipboard access blocked — the input stays typable */
    }
  };
  const test = () => {
    if (testState !== "idle") return;
    setTestState("testing");
    window.setTimeout(() => setTestState("ok"), 700);
  };
  return (
    <div>
      <DrawerHeading>Billing Setup</DrawerHeading>
      <DrawerBody>
        Connect the payment provider you already use so the Grid cockpit can
        collect funds the moment a job wraps.
      </DrawerBody>

      <ol
        style={{
          margin: "0 0 14px",
          paddingLeft: 22,
          color: IVORY,
          fontSize: "0.94rem",
          lineHeight: 1.6,
        }}
      >
        <li>Log into your Stripe dashboard</li>
        <li>Copy your <span style={{ color: GOLD, fontWeight: 700 }}>Secret API key</span></li>
        <li>Paste it into the field below</li>
      </ol>

      <label
        htmlFor="billing-stripe-key"
        style={{
          display: "block",
          color: MUTED,
          fontSize: 11,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Stripe API Key
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          id="billing-stripe-key"
          data-testid="billing-stripe-key-input"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="sk_live_..."
          type="password"
          style={{
            flex: "1 1 220px",
            boxSizing: "border-box",
            background: NAVY,
            color: IVORY,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 8,
            padding: "12px 14px",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.95rem",
            outline: "none",
          }}
          className="ac-textarea"
        />
        <button
          type="button"
          data-testid="billing-paste-btn"
          onClick={paste}
          style={{
            background: "transparent",
            color: GOLD,
            border: `1px solid ${GOLD_SOFT}`,
            padding: "0 16px",
            minHeight: 42,
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontWeight: 800,
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Paste
        </button>
      </div>

      <div
        style={{
          marginTop: 14,
          background: NAVY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 10,
          padding: 10,
        }}
      >
        <CopyRow
          testKey="billing-webhook"
          label="Grid Webhook"
          value="https://grid.internal/billing/connect"
        />
      </div>

      <button
        type="button"
        data-testid="billing-test-btn"
        onClick={test}
        disabled={testState !== "idle" || !key.trim()}
        style={{
          marginTop: 14,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 20px",
          background:
            testState === "ok"
              ? GREEN
              : testState === "testing"
              ? "rgba(255,255,255,0.06)"
              : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_HI} 100%)`,
          color:
            testState === "ok"
              ? "#062B14"
              : testState === "testing"
              ? MUTED
              : "#0F0A00",
          border: "none",
          borderRadius: 10,
          fontWeight: 800,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontSize: 13,
          cursor:
            testState !== "idle" || !key.trim() ? "not-allowed" : "pointer",
          opacity: !key.trim() && testState === "idle" ? 0.55 : 1,
          boxShadow:
            testState === "idle" ? `0 12px 24px -10px ${GOLD_SOFT}` : "none",
          transition: "all 220ms ease",
        }}
      >
        {testState === "ok" && <span aria-hidden>✓</span>}
        {testState === "idle" && "Test Connection"}
        {testState === "testing" && "Testing…"}
        {testState === "ok" && "Connection Verified"}
      </button>
      <SpeakerAudioPlaceholder speaker="Sterling" />
    </div>
  );
}

// ---- Dispatch 2 · Direct Invoicing ----
const HANDSHAKE_TEMPLATE =
  "Hi [Client Name], here's your invoice for [Service]. Tap below to pay securely.";
function DirectInvoicingDrawer() {
  const [msg, setMsg] = useState(HANDSHAKE_TEMPLATE);
  const [generated, setGenerated] = useState(false);
  const [sent, setSent] = useState(false);
  const generate = () => setGenerated(true);
  const send = () => {
    setSent(true);
    window.setTimeout(() => setSent(false), 2200);
  };
  return (
    <div>
      <DrawerHeading>Direct Invoicing</DrawerHeading>
      <DrawerBody>
        Send a pre-written Handshake Message that physically generates the bill
        for the client — no chasing, no phone tag.
      </DrawerBody>

      <div
        style={{
          padding: "12px 14px",
          background: GOLD_INK,
          border: `1px solid ${GOLD_SOFT}`,
          borderRadius: 10,
          color: IVORY,
          fontSize: "0.95rem",
          lineHeight: 1.55,
          fontStyle: "italic",
          fontFamily: SERIF,
          marginBottom: 12,
        }}
        data-testid="invoicing-template-card"
      >
        <span style={{ color: GOLD, fontWeight: 800, fontStyle: "normal", letterSpacing: "0.16em", textTransform: "uppercase", fontSize: 11, display: "block", marginBottom: 4 }}>
          Handshake Message
        </span>
        {HANDSHAKE_TEMPLATE}
      </div>

      <label
        htmlFor="invoicing-editor"
        style={{
          display: "block",
          color: MUTED,
          fontSize: 11,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Customize Message
      </label>
      <textarea
        id="invoicing-editor"
        data-testid="invoicing-editor"
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        rows={3}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: NAVY,
          color: IVORY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 8,
          padding: "12px 14px",
          fontSize: "0.95rem",
          lineHeight: 1.5,
          outline: "none",
          resize: "vertical",
          minHeight: 84,
        }}
        className="ac-textarea"
      />

      <button
        type="button"
        data-testid="invoicing-generate-btn"
        onClick={generate}
        style={{
          marginTop: 12,
          width: "100%",
          padding: "14px 20px",
          background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_HI} 100%)`,
          color: "#0F0A00",
          border: "none",
          borderRadius: 10,
          fontWeight: 800,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontSize: 14,
          cursor: "pointer",
          boxShadow: `0 14px 30px -12px ${GOLD_SOFT}`,
        }}
      >
        Generate Invoice
      </button>

      {generated && (
        <div
          data-testid="invoicing-preview-card"
          style={{
            marginTop: 14,
            padding: 16,
            background: NAVY,
            border: `1px solid ${GOLD_SOFT}`,
            borderRadius: 12,
          }}
        >
          <div
            style={{
              color: GOLD,
              fontSize: 10,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              fontWeight: 800,
              marginBottom: 8,
            }}
          >
            Client Preview
          </div>
          <div
            style={{
              color: IVORY,
              fontSize: "0.94rem",
              lineHeight: 1.55,
              marginBottom: 12,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {msg}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 12px",
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 8,
              marginBottom: 12,
              gap: 10,
            }}
          >
            <span style={{ color: MUTED, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>
              Amount
            </span>
            <span
              data-testid="invoicing-amount"
              style={{
                color: IVORY,
                fontFamily: SERIF,
                fontSize: "1.4rem",
                fontWeight: 700,
              }}
            >
              $ [Amount]
            </span>
          </div>
          <button
            type="button"
            data-testid="invoicing-pay-btn"
            disabled
            style={{
              width: "100%",
              padding: "12px 16px",
              background: GREEN,
              color: "#062B14",
              border: "none",
              borderRadius: 8,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontSize: 12,
              cursor: "not-allowed",
              opacity: 0.85,
            }}
          >
            Pay Securely
          </button>
        </div>
      )}

      {generated && (
        <button
          type="button"
          data-testid="invoicing-send-btn"
          onClick={send}
          style={{
            marginTop: 12,
            width: "100%",
            padding: "12px 20px",
            background: sent ? GREEN : "transparent",
            color: sent ? "#062B14" : GOLD,
            border: `1px solid ${sent ? GREEN : GOLD_SOFT}`,
            borderRadius: 10,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontSize: 12,
            cursor: "pointer",
            transition: "all 220ms ease",
          }}
        >
          {sent ? "Invoice Sent ✓" : "Send Handshake"}
        </button>
      )}
      <SpeakerAudioPlaceholder speaker="Sterling" />
    </div>
  );
}

// ---- Dispatch 3 · Client Notes & Upsells ----
function ClientIntelDrawer() {
  const [note, setNote] = useState("");
  const [flagged, setFlagged] = useState(false);
  const [upsell, setUpsell] = useState(true);
  return (
    <div>
      <DrawerHeading>Client Notes &amp; Upsells</DrawerHeading>
      <DrawerBody>
        Log specific client needs — the Grid flags them for future outreach so
        no revenue slips through the cracks.
      </DrawerBody>

      <div
        style={{
          padding: "12px 14px",
          background: GOLD_INK,
          border: `1px solid ${GOLD_SOFT}`,
          borderRadius: 10,
          marginBottom: 12,
        }}
        data-testid="intel-example-scenario"
      >
        <span style={{ color: GOLD, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 800, display: "block", marginBottom: 4 }}>
          Example scenario
        </span>
        <span style={{ color: IVORY, fontStyle: "italic", fontFamily: SERIF, fontSize: "0.98rem", lineHeight: 1.5 }}>
          &ldquo;Customer mentioned wanting a patio next spring.&rdquo;
        </span>
      </div>

      <label
        htmlFor="intel-note"
        style={{
          display: "block",
          color: MUTED,
          fontSize: 11,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Client Note
      </label>
      <textarea
        id="intel-note"
        data-testid="intel-note-input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Log a client note..."
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: NAVY,
          color: IVORY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 8,
          padding: "12px 14px",
          fontSize: "0.95rem",
          lineHeight: 1.5,
          outline: "none",
          resize: "vertical",
          minHeight: 64,
        }}
        className="ac-textarea"
      />

      <button
        type="button"
        data-testid="intel-flag-btn"
        onClick={() => setFlagged(true)}
        style={{
          marginTop: 12,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          background: flagged ? GREEN : "transparent",
          color: flagged ? "#062B14" : GOLD,
          border: `1px solid ${flagged ? GREEN : GOLD_SOFT}`,
          borderRadius: 8,
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontSize: 12,
          cursor: "pointer",
          transition: "all 220ms ease",
        }}
      >
        {flagged ? "✓ Follow-Up Scheduled · March 2027" : "Flag for Follow-Up"}
      </button>

      {flagged && (
        <div
          data-testid="intel-followup-card"
          style={{
            marginTop: 12,
            padding: "12px 14px",
            background: "rgba(74,222,128,0.10)",
            border: "1px solid rgba(74,222,128,0.55)",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span aria-hidden style={{ fontSize: 20 }}>🔔</span>
          <span style={{ color: IVORY, fontSize: "0.94rem", lineHeight: 1.5, flex: 1 }}>
            <strong style={{ color: GREEN }}>March Follow-Up:</strong> Call
            {" "}<span style={{ color: GOLD }}>[Client]</span> about patio project
          </span>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: GREEN,
              color: "#062B14",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 900,
              flexShrink: 0,
            }}
          >
            ✓
          </span>
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 14px",
          background: upsell ? GOLD_INK : "rgba(255,255,255,0.02)",
          border: `1px solid ${upsell ? GOLD_SOFT : HAIRLINE}`,
          borderRadius: 10,
        }}
      >
        <ToggleSwitch
          value={upsell}
          onChange={setUpsell}
          testId="intel-upsell-toggle"
          label="Smart Upsell"
        />
        <div style={{ flex: 1 }}>
          <div style={{ color: IVORY, fontSize: "0.95rem", fontWeight: 700 }}>
            Smart Upsell
          </div>
          <div style={{ color: MUTED, fontSize: "0.82rem", marginTop: 2, lineHeight: 1.5 }}>
            Grid will auto-flag upsell opportunities from your notes.
          </div>
        </div>
      </div>
      <SpeakerAudioPlaceholder speaker="Sterling" />
    </div>
  );
}

// ---- Dispatch 4 · Dispatch Lockdown ----
function DispatchConfirmDrawer({ checks }) {
  const summary = [
    { key: "dispatch:0", label: "Billing Setup" },
    { key: "dispatch:1", label: "Direct Invoicing" },
    { key: "dispatch:2", label: "Client Notes & Upsells" },
  ];
  return (
    <div>
      <DrawerHeading>Dispatch Lockdown</DrawerHeading>
      <DrawerBody>
        Review your Dispatch configuration. Once confirmed, Sterling takes the
        wheel.
      </DrawerBody>
      <StatusSummary rows={summary} checks={checks} />
      <SpeakerAudioPlaceholder speaker="Sterling" mode="confirms" />
    </div>
  );
}

// ---------- Growth drawer bodies (Step 03 · Vance & Sloan) ----------
function GrowthDrawerContent({ index, task, checks }) {
  switch (index) {
    case 0:
      return <GrowthContentEngineDrawer />;
    case 1:
      return <GrowthSocialDrawer />;
    case 2:
      return <GrowthLibraryDrawer />;
    case 3:
      return <GrowthConfirmDrawer checks={checks} />;
    default:
      return (
        <div>
          <DrawerHeading>{task}</DrawerHeading>
        </div>
      );
  }
}

// ---- Growth 1 · Content Engine ----
const GROWTH_TONES = ["Professional", "Casual", "Inspirational", "Direct"];
function GrowthContentEngineDrawer() {
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState(GROWTH_TONES[0]);
  return (
    <div>
      <DrawerHeading>Content Engine</DrawerHeading>
      <DrawerBody>
        Activate Vance&apos;s content engine — he&apos;ll draft posts, emails,
        and sequences for your brand.
      </DrawerBody>
      <label
        htmlFor="growth-keywords"
        style={{
          display: "block",
          color: MUTED,
          fontSize: 11,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Brand Voice Keywords
      </label>
      <input
        id="growth-keywords"
        data-testid="growth-keywords-input"
        value={keywords}
        onChange={(e) => setKeywords(e.target.value)}
        placeholder="e.g. reliable, no-fluff, family-owned"
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: NAVY,
          color: IVORY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 8,
          padding: "12px 14px",
          fontSize: "0.95rem",
          outline: "none",
        }}
        className="ac-textarea"
      />

      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
        data-testid="growth-tone-selector"
      >
        {GROWTH_TONES.map((t) => {
          const active = tone === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTone(t)}
              data-testid={`growth-tone-${t.toLowerCase()}`}
              style={{
                padding: "9px 14px",
                borderRadius: 999,
                background: active ? GOLD : "transparent",
                color: active ? "#0F0A00" : IVORY,
                border: `1px solid ${active ? GOLD : HAIRLINE}`,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: "pointer",
                transition: "all 180ms ease",
              }}
            >
              {t}
            </button>
          );
        })}
      </div>
      <SpeakerAudioPlaceholder speaker="Vance" />
    </div>
  );
}

// ---- Growth 2 · Social Mapping ----
const GROWTH_SOCIALS = [
  { key: "facebook", label: "Facebook", url: "https://grid.internal/connect/facebook" },
  { key: "linkedin", label: "LinkedIn", url: "https://grid.internal/connect/linkedin" },
  { key: "instagram", label: "Instagram", url: "https://grid.internal/connect/instagram" },
];
function GrowthSocialDrawer() {
  const [connected, setConnected] = useState({});
  return (
    <div>
      <DrawerHeading>Social Mapping</DrawerHeading>
      <DrawerBody>
        Map your social accounts so Vance knows where to publish. Everything
        routes through internal Grid tools — no external OAuth.
      </DrawerBody>
      <div style={{ display: "grid", gap: 8 }}>
        {GROWTH_SOCIALS.map((s) => (
          <div
            key={s.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                color: IVORY,
                fontSize: "0.95rem",
                fontWeight: 700,
                minWidth: 100,
              }}
            >
              {s.label}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 180,
                color: MUTED,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.82rem",
                wordBreak: "break-all",
              }}
            >
              {s.url}
            </span>
            <SocialCopyButton value={s.url} testKey={s.key} />
            <button
              type="button"
              data-testid={`growth-social-confirm-${s.key}`}
              onClick={() =>
                setConnected((c) => ({ ...c, [s.key]: !c[s.key] }))
              }
              style={{
                background: connected[s.key] ? GREEN : "transparent",
                color: connected[s.key] ? "#062B14" : GOLD,
                border: `1px solid ${connected[s.key] ? GREEN : GOLD_SOFT}`,
                padding: "6px 12px",
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: 800,
                borderRadius: 6,
                cursor: "pointer",
                transition: "all 200ms ease",
              }}
            >
              {connected[s.key] ? "Connected" : "Connect"}
            </button>
          </div>
        ))}
      </div>
      <SpeakerAudioPlaceholder speaker="Vance" />
    </div>
  );
}
function SocialCopyButton({ value, testKey }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* noop */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={`copy-btn-${testKey}`}
      style={{
        background: copied ? GREEN : "transparent",
        color: copied ? "#062B14" : GOLD,
        border: `1px solid ${copied ? GREEN : GOLD_SOFT}`,
        padding: "6px 12px",
        fontSize: 11,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        fontWeight: 800,
        borderRadius: 6,
        cursor: "pointer",
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ---- Growth 3 · Sloan's Technical Library ----
function GrowthLibraryDrawer() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const openPicker = () => inputRef.current && inputRef.current.click();
  return (
    <div>
      <DrawerHeading>Sloan&apos;s Technical Library</DrawerHeading>
      <DrawerBody>
        Let Sloan index your existing docs, SOPs, and knowledge base so the
        Grid can reference them.
      </DrawerBody>

      <div
        data-testid="growth-library-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onClick={openPicker}
        style={{
          padding: "24px 18px",
          border: `2px dashed ${dragging ? GOLD : GOLD_SOFT}`,
          borderRadius: 12,
          background: dragging ? GOLD_INK : "rgba(255,255,255,0.02)",
          color: IVORY,
          textAlign: "center",
          cursor: "pointer",
          transition: "all 220ms ease",
        }}
      >
        <div style={{ fontSize: 28, color: GOLD, marginBottom: 6 }} aria-hidden>
          ⤴
        </div>
        <div style={{ fontWeight: 700, fontSize: "0.98rem" }}>
          Drop files here — or click to browse
        </div>
        <div style={{ color: MUTED, fontSize: "0.82rem", marginTop: 4 }}>
          PDF, DOCX, TXT — Sloan reads them all
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt"
          style={{ display: "none" }}
          data-testid="growth-library-file-input"
          onChange={() => {
            /* placeholder — real ingest wires in later */
          }}
        />
      </div>

      <div
        style={{
          marginTop: 12,
          background: NAVY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 10,
          padding: 10,
        }}
      >
        <CopyRow
          testKey="library-endpoint"
          label="Library Endpoint"
          value="https://grid.internal/library/index"
        />
      </div>
      <SpeakerAudioPlaceholder speaker="Sloan" />
    </div>
  );
}

// ---- Growth 4 · Growth Lockdown ----
function GrowthConfirmDrawer({ checks }) {
  const summary = [
    { key: "growth:0", label: "Content Engine" },
    { key: "growth:1", label: "Social Mapping" },
    { key: "growth:2", label: "Technical Library Index" },
  ];
  return (
    <div>
      <DrawerHeading>Growth Lockdown</DrawerHeading>
      <DrawerBody>
        Review your Growth configuration. Once confirmed, Vance and Sloan go
        live.
      </DrawerBody>
      <StatusSummary rows={summary} checks={checks} />
      <SpeakerAudioPlaceholder
        speaker="Vance & Sloan"
        mode="confirm"
      />
    </div>
  );
}

// ---------- Shared drawer helpers ----------
function StatusSummary({ rows, checks }) {
  return (
    <div
      data-testid="drawer-status-summary"
      style={{
        display: "grid",
        gap: 8,
        padding: 12,
        background: NAVY,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 10,
      }}
    >
      {rows.map((r) => {
        const done = !!checks[r.key];
        return (
          <div
            key={r.key}
            data-testid={`status-row-${r.key.replace(":", "-")}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 10px",
              background: done ? "rgba(74,222,128,0.08)" : "rgba(255,255,255,0.02)",
              border: `1px solid ${done ? "rgba(74,222,128,0.55)" : HAIRLINE}`,
              borderRadius: 8,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: done ? GREEN : "transparent",
                border: `2px solid ${done ? GREEN : GOLD_SOFT}`,
                color: done ? "#062B14" : GOLD_SOFT,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 900,
                flexShrink: 0,
              }}
            >
              {done ? "✓" : "…"}
            </span>
            <span style={{ color: IVORY, fontSize: "0.95rem", fontWeight: 600 }}>
              {r.label}
            </span>
            <span
              style={{
                marginLeft: "auto",
                color: done ? GREEN : MUTED,
                fontSize: 10,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                fontWeight: 800,
              }}
            >
              {done ? "Ready" : "Pending"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ToggleSwitch({ value, onChange, testId, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label}
      data-testid={testId}
      onClick={() => onChange(!value)}
      style={{
        width: 46,
        height: 26,
        borderRadius: 999,
        background: value ? GOLD : "rgba(255,255,255,0.08)",
        border: `1px solid ${value ? GOLD_SOFT : HAIRLINE}`,
        position: "relative",
        cursor: "pointer",
        transition: "all 220ms ease",
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 2,
          left: value ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: value ? "#0F0A00" : IVORY,
          transition: "left 220ms ease, background 220ms ease",
        }}
      />
    </button>
  );
}

function SpeakerAudioPlaceholder({ speaker, mode = "explains" }) {
  return (
    <div
      data-testid={`audio-placeholder-${speaker
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}`}
      style={{
        marginTop: 14,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        borderRadius: 999,
        background: "rgba(212,175,55,0.08)",
        border: `1px solid ${GOLD_SOFT}`,
        color: GOLD,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>▶</span>
      🎧 {speaker} {mode} (10s)
    </div>
  );
}

// ---------- localStorage helpers ----------
function loadProgress() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveProgress(state) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// ---------- Scoped styles ----------
const ACADEMY_CSS = `
  @keyframes ac-fall {
    0%   { transform: translateY(-40px) rotate(0deg);   opacity: 1; }
    100% { transform: translateY(105vh)  rotate(720deg); opacity: 0; }
  }
  @keyframes ac-dot {
    0%,80%,100% { opacity:.25; transform:translateY(0); }
    40% { opacity:1; transform:translateY(-3px); }
  }
  .ac-dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:${BLUE}; margin:0 3px; animation: ac-dot 1.1s ease-in-out infinite; }
  .ac-dot:nth-child(2){ animation-delay:.15s; }
  .ac-dot:nth-child(3){ animation-delay:.3s; }
  .ac-textarea:focus { border-color: ${BLUE} !important; box-shadow: 0 0 0 3px rgba(79,195,247,0.20); }
  @media (max-width: 640px) {
    /* Ensure thumb-friendly targets everywhere on mobile. */
    [data-testid^="academy-task-"] { min-height: 52px; }
  }
`;
